import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Mic, Square, Loader } from 'lucide-react';

export interface DescriptionBoxProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Show AI Cleanup button */
  showAiCleanup?: boolean;
  /** Show Azure dictation (voice input) button */
  showDictation?: boolean;
  /** Hint text for AI context (e.g. "project description") */
  aiContextHint?: string;
}

export const DescriptionBox: React.FC<DescriptionBoxProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Enter description...',
  rows = 4,
  required = false,
  disabled = false,
  className = '',
  id,
  showAiCleanup = true,
  showDictation = true,
  aiContextHint = 'description',
}) => {
  const [isCleaning, setIsCleaning] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [dictationError, setDictationError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognizerRef = useRef<any>(null);
  const confirmedTextRef = useRef<string>('');
  const valueAtDictationStartRef = useRef<string>('');
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleAiCleanup = async () => {
    if (!value?.trim()) return;
    setIsCleaning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/ai/cleanup-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          text: value,
          context: aiContextHint,
        }),
      });
      const data = await response.json();
      if (data.success && data.data?.cleanedText) {
        onChange(data.data.cleanedText);
      } else {
        throw new Error(data.message || 'Cleanup failed');
      }
    } catch (err) {
      console.error('AI Cleanup error:', err);
      setDictationError(err instanceof Error ? err.message : 'AI cleanup failed');
      setTimeout(() => setDictationError(null), 4000);
    } finally {
      setIsCleaning(false);
    }
  };

  const startDictation = async () => {
    if (isRecording) {
      stopDictation();
      return;
    }

    try {
      setDictationError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/ai/azure-speech-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to get speech credentials');
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Speech not configured');

      const { key, region } = data.data;

      const SpeechSDK = await import('microsoft-cognitiveservices-speech-sdk');
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(key, region);
      speechConfig.speechRecognitionLanguage = 'en-NZ';
      speechConfig.enableDictation();
      speechConfig.setServiceProperty('punctuation', 'explicit', SpeechSDK.ServicePropertyChannel.UriQueryParameter);

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      recognizerRef.current = recognizer;
      confirmedTextRef.current = '';
      valueAtDictationStartRef.current = value;

      recognizer.recognizing = (_s: any, e: any) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech && e.result.text) {
          const interim = e.result.text;
          const base = valueAtDictationStartRef.current;
          const confirmed = confirmedTextRef.current;
          const displayText = confirmed ? `${confirmed} ${interim}` : interim;
          const newValue = base ? `${base} ${displayText}` : displayText;
          onChange(newValue);
        }
      };

      recognizer.recognized = (_s: any, e: any) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const recognizedText = (e.result as any).privJson?.DisplayText || e.result.text;
          if (recognizedText) {
            confirmedTextRef.current = confirmedTextRef.current
              ? `${confirmedTextRef.current} ${recognizedText}`
              : recognizedText;
            const base = valueAtDictationStartRef.current;
            const newValue = base ? `${base} ${confirmedTextRef.current}` : confirmedTextRef.current;
            onChange(newValue);
          }
        }
      };

      recognizer.canceled = (_s: any, e: any) => {
        setDictationError(e.errorDetails || 'Recognition stopped');
        setIsRecording(false);
      };

      recognizer.sessionStarted = () => {
        setIsRecording(true);
        setDictationError(null);
      };

      recognizer.sessionStopped = () => {
        setIsRecording(false);
      };

      await recognizer.startContinuousRecognitionAsync();
    } catch (err) {
      console.error('Dictation error:', err);
      setDictationError(err instanceof Error ? err.message : 'Failed to start dictation');
      setIsRecording(false);
    }
  };

  const stopDictation = () => {
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync(() => {
        recognizerRef.current?.close();
        recognizerRef.current = null;
        setIsRecording(false);
      });
    } else {
      setIsRecording(false);
    }
  };

  useEffect(() => {
    return () => {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          recognizerRef.current?.close();
          recognizerRef.current = null;
        });
      }
    };
  }, []);

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
          {required && <span className="text-error-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          required={required}
          disabled={disabled}
          className="w-full px-3 py-2 pr-24 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-y min-h-[80px]"
        />
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {showAiCleanup && (
            <button
              type="button"
              onClick={handleAiCleanup}
              disabled={disabled || !value?.trim() || isCleaning}
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="AI Cleanup - Fix grammar, punctuation, and formatting"
            >
              {isCleaning ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
            </button>
          )}
          {showDictation && (
            <button
              type="button"
              onClick={isRecording ? stopDictation : startDictation}
              disabled={disabled}
              className={`p-2 rounded-lg transition-colors ${
                isRecording
                  ? 'text-error-600 dark:text-error-400 bg-red-50 dark:bg-gray-700 animate-pulse'
                  : 'text-gray-500 dark:text-gray-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-gray-100 dark:hover:bg-gray-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={isRecording ? 'Stop dictation' : 'Start voice input (Azure)'}
            >
              {isRecording ? (
                <Square className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </div>
      {dictationError && (
        <p className="mt-1 text-xs text-error-600 dark:text-error-400">{dictationError}</p>
      )}
    </div>
  );
};
